import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useLocation } from "react-router-dom";
import { api, PLANS } from "../lib/apiClient";
import { Header, Footer } from "../components/Layout";
import { Check, X } from "lucide-react";

const ACTION_MAP = {
  lrar_letter: "lrar",
  cerfa_pdf: "cerfa",
  kit_audience: "kit_audience",
};

export function Checkout() {
  const { lookupKey } = useParams();
  const [params] = useSearchParams();
  const caseId = params.get("case_id");
  const plan = PLANS[lookupKey];
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const pay = async () => {
    setLoading(true); setErr("");
    try {
      const body = { lookup_key: lookupKey, origin_url: window.location.origin };
      if (caseId) body.case_id = caseId;
      if (ACTION_MAP[lookupKey]) body.action_type = ACTION_MAP[lookupKey];
      const r = await api.post("/payments/checkout", body);
      window.location.href = r.data.checkout_url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur checkout");
      setLoading(false);
    }
  };

  if (!plan) return <div className="min-h-screen"><Header /><div className="max-w-md mx-auto p-10">Plan introuvable</div><Footer /></div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-md mx-auto px-4 pt-16 pb-24">
        <div className="j-card" data-testid="checkout-card">
          <div className="j-label mb-2">Confirmation de commande</div>
          <h1 className="j-h2 font-heading">{plan.name}</h1>
          <div className="font-heading font-black text-4xl text-[#10B981] mt-3">{plan.price}</div>
          <p className="text-sm text-slate-600 mt-3">{plan.desc}</p>
          {caseId && <div className="text-xs text-slate-400 mt-2">Dossier lié : {caseId}</div>}

          <ul className="text-sm text-slate-700 mt-6 space-y-2">
            <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981]" /> Génération instantanée</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981]" /> Evidence Score sur chaque source</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981]" /> Export PDF illimité</li>
          </ul>

          {err && <div className="text-red-600 text-sm mt-4">{err}</div>}
          <button onClick={pay} disabled={loading} className="btn-emerald w-full mt-6" data-testid="pay-now-btn">
            {loading ? "Redirection…" : `${plan.cta} — ${plan.price}`}
          </button>
          <div className="text-xs text-slate-400 mt-3 text-center">Paiement sécurisé Stripe · Carte test : 4242 4242 4242 4242</div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [action, setAction] = useState(null);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          // If transaction was for an action (LRAR/Cerfa/Kit), create the action
          if (r.data.case_id && r.data.action_type) {
            try {
              const a = await api.post("/actions/create", {
                case_id: r.data.case_id, action_type: r.data.action_type,
              });
              setAction(a.data);
            } catch (e) {
              setAction({ error: e.response?.data?.detail || "Action non créée" });
            }
          }
          return;
        }
      } catch (e) {
        console.warn("Payment status poll error:", e);
      }
      tries++;
      if (tries < 15) setTimeout(poll, 1500);
      else setStatus("timeout");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-md mx-auto px-4 pt-16 pb-24">
        <div className="j-card text-center" data-testid="payment-success-card">
          {status === "checking" && (<>
            <div className="pulse-dot mx-auto mb-4"></div>
            <div className="font-heading font-black text-xl">Vérification du paiement…</div>
          </>)}
          {status === "paid" && (<>
            <div className="w-14 h-14 rounded-full bg-[#10B981] mx-auto flex items-center justify-center mb-4"><Check className="w-7 h-7 text-white" /></div>
            <h1 className="j-h2 font-heading">Paiement confirmé</h1>
            <p className="text-slate-600 mt-2">Votre accès est activé.</p>
            {action && !action.error && (
              <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-left text-sm" data-testid="action-confirmation">
                <div className="font-heading font-bold text-emerald-800">Action enregistrée</div>
                <div className="text-emerald-700 text-xs mt-1">Type: {action.action_type}</div>
                {action.tracking_number && <div className="text-emerald-700 text-xs">Suivi La Poste : <span className="font-mono">{action.tracking_number}</span></div>}
                <div className="text-emerald-700 text-xs">Statut: {action.status}</div>
                {action.action_type === "cerfa" && (
                  <a href={`/api/actions/${action.action_id}/pdf`} target="_blank" rel="noopener noreferrer" className="btn-emerald mt-3 w-full !text-sm" data-testid="download-cerfa-btn">
                    📄 Télécharger le Cerfa PDF
                  </a>
                )}
                {action.action_type === "kit_audience" && (
                  <a href={`/avocats?case_id=${action.case_id}`} className="btn-emerald mt-3 w-full !text-sm" data-testid="choose-lawyer-btn">
                    ⚖️ Choisir mon avocat partenaire
                  </a>
                )}
              </div>
            )}
            {action?.error && (
              <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">{action.error}</div>
            )}
            <Link to="/dashboard" className="btn-emerald mt-6 w-full" data-testid="goto-dashboard-btn">Aller au Dashboard</Link>
          </>)}
          {status === "timeout" && (<>
            <div className="text-amber-600 font-semibold">Paiement en cours de vérification.</div>
            <Link to="/dashboard" className="btn-navy mt-6">Dashboard</Link>
          </>)}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-md mx-auto px-4 pt-16 pb-24">
        <div className="j-card text-center" data-testid="payment-cancel-card">
          <div className="w-14 h-14 rounded-full bg-slate-100 mx-auto flex items-center justify-center mb-4"><X className="w-7 h-7 text-slate-500" /></div>
          <h1 className="j-h2 font-heading">Paiement annulé</h1>
          <p className="text-slate-600 mt-2">Aucun montant n'a été prélevé.</p>
          <Link to="/" className="btn-navy mt-6 w-full">Retour à l'accueil</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
